/** JSON */
import i18n from './../assets/json/i18n.json';

/** Helpers */
import { 
  stringToObjectPath, 
  isValidJSON, 
  isNullOrUndefined 
} from './functions.js';

/**
 * Update filter datas for url
 * @param {Object} filterTypes 
 * @param {String} filterName 
 * @param {String} dataField 
 * @param {String} filterLabel 
 * @param {Object} filterDatas 
 * @returns Array
 */
export const updateFilterDatas = ( currentFilters, filterTypes, filterName, dataField, filterLabel, filterDatas ) => 
{
  // get filters param except current filter name
  const filtersParams = currentFilters.filter( elem => elem.filterName !== filterName );

  // init newFilters var
  let newFilters = [ ...filtersParams ];

  // update current filters with new filter if filterDatas is not empty
  if( filterDatas !== null )
  {
    newFilters = [ ...filtersParams, {
      types: filterTypes,
      filterName: filterName,
      dataField: dataField,
      label: filterLabel,
      values: filterDatas
    }];
  }

  return newFilters;
}

/**
 * Filter data array with filters array
 * @param {Array} datas Array of data from webservice
 * @param {Array} filters Array of filters
 * @returns Array
 */
export const filterDatas = ( datas, filters ) => 
{
  let filtersDatas = datas;

  if( 
    datas !== null 
    && filters !== null
    && Array.isArray( datas )
    && Array.isArray( filters ) 
  ){
    // clone original data source
    let cloneDatas = [ ...datas ];

    // for each filter
    filters.forEach(  filter => 
    {
      // get filter type
      const filterValues = filter.values;

      // for each type
      Object.keys( filterValues ).forEach( filterValueField => 
      {
        // filter clone datas
        cloneDatas = [ ...cloneDatas ].filter( elem => 
        {
          // get filter type
          const filterType = filter.types[ filterValueField ];

          // get filter value
          const filterValue = filter.values[ filterValueField ];

          // get data value
          let dataValue = stringToObjectPath( elem, filter.dataField );

          // parse dataValue to json if is string
          if( typeof dataValue === 'string' && isValidJSON( dataValue ) )
            dataValue = JSON.parse( dataValue );

          if( 
            dataValue !== undefined 
            && dataValue !== null 
            && filterValue !== null
            && applyFilter( filterType, dataValue[filterValueField], filterValue ) === true
          )
            return true;
          else
            return false;
        });   
      });
    });

    // set filters data with filters clone datas
    filtersDatas = cloneDatas;
  }

  return filtersDatas;
}

/**
 * Apply filters rules by type
 * @param {String} filterType filter type
 * @param {Any} dataValue value to filtered
 * @param {Any} filterValue value for filtering
 * @returns boolean
 */
export const applyFilter = ( filterType, dataValue, filterValue ) => 
{
  let response = false;

  switch ( filterType )
  {
    case 'range':
      response = rangeFilter( dataValue, filterValue );
      break;

    case 'range-ctr':
    case 'range-ranked-rate':
      response = rangePercentFilter( dataValue, filterValue );
      break;

    case 'range-position':
      response = rangePositionFilter( dataValue, filterValue );
      break;

    case 'variation':
      response = variationFilter( dataValue, filterValue );
      break;

    case 'string':
      response = stringFilter( dataValue, filterValue );
      break;

    case 'string-in-array':
      response = stringInArray( dataValue, filterValue );
      break;

    case 'snippets':
      response = snippetFilter( dataValue, filterValue );
      break;

    case 'status':
      response = statusFilter( dataValue, filterValue );
      break;

    case 'categories':
      response = categoriesFilter( dataValue, filterValue );
      break;

    case 'keywords-groups':
      response = keywordsGroupsFilter( dataValue, filterValue );
      break;
  
    default:
      response = dataValue === filterValue;
      break;
  }  

  return response;
}

/**
 * Filter datas by range values
 * @param {Array} dataValue 
 * @param {Array} filterValue [0,10]
 * @returns boolean
 */
export const rangeFilter = ( dataValue, filterValue ) => 
{
  if( 
    filterValue[0] !== undefined
    && filterValue[1] !== undefined
    && dataValue !== null
    && filterValue[0] <= dataValue 
    && dataValue <= filterValue[1] 
  )
    return true;
  else
    return false;
}

/**
 * Filter datas by range values
 * @param {Array} dataValue 
 * @param {Array} filterValue [0,10]
 * @returns boolean
 */
export const rangePercentFilter = ( dataValue, filterValue ) => 
{
  if( 
    filterValue[0] !== undefined
    && filterValue[1] !== undefined
    && dataValue !== null
    && filterValue[0] <= ( dataValue * 100 )
    && ( dataValue * 100 ) <= filterValue[1] 
  )
    return true;
  else
    return false;
}

/**
 * Filter datas by range values with specific position case NaN
 * @param {Array} dataValue 
 * @param {Array} filterValue [0,10]
 * @returns boolean
 */
export const rangePositionFilter = ( dataValue, filterValue ) => 
  {
    if( 
      (
        filterValue[0] !== undefined
        && filterValue[1] !== undefined
        && dataValue !== null
        && filterValue[0] <= dataValue 
        && dataValue <= filterValue[1] 
      ) || (
        isNaN( dataValue )
        && filterValue[0] === 100
        && filterValue[1] === 101
      )
    )
      return true;
    else
      return false;
  }

/**
 * Filter datas with variation
 * @param {Array} dataValue 
 * @param {String} filterValue increase | decrease | constant
 * @returns 
 */
export const variationFilter = ( dataValue, filterValue ) =>
{
  if( filterValue === 'increase' && dataValue > 0 ) return true;
  else if ( filterValue === 'decrease' && dataValue < 0 ) return true;
  else if( filterValue === 'constant' && dataValue === 0 ) return true;
  else return false;
}

/**
 * Filters datas on string with reg exp
 * @param {Array} dataValue 
 * @param {Object} { value: moulin, filterStringType: equalsIgnoreCase}
 * @returns 
 */
export const stringFilter = ( dataValue, filterValue ) => 
{
  // get value
  const value = filterValue.value;

  // get filterStringType
  const filterStringType = filterValue.filterStringType;

  if( filterStringType === 'equalsIgnoreCase' )
    return new RegExp( '^' + value + '$', 'gi' ).test( dataValue );
  else if( filterStringType === 'startsWithIgnoreCase' )
    return new RegExp( '^' + value, 'gi' ).test( dataValue );
  else if( filterStringType === 'doesNotContainIgnoreCase' )
    return !new RegExp( value, 'gi' ).test( dataValue );
  else
    return new RegExp( value, 'gi' ).test( dataValue );
}

/**
 * 
 * @param {Array} dataValue 
 * @param {Object} filterValue { values: [], filterOn: 'notOnSerp' }
 * @returns {Boolean}
 */
export const snippetFilter = ( dataValue, filterValue ) =>
{
  // get values
  const values = filterValue.values.map( elem => elem.value );

  // get filterOn
  const filterOn = filterValue.filterOn;

  if( dataValue !== null && dataValue !== 'unscrap' )
  {
    // only presence value is selected
    if( 
      values === null 
      || ( Array.isArray( values ) && values.length === 0 ) 
    ){
      if( filterOn === 'onSerp' )
        return ( dataValue.valueMe !== null || dataValue.valueOthers !== null );
      else if( filterOn === 'notOnSerp' )
        return ( dataValue.valueOthers === null && dataValue.valueOthers === null );
      else if( filterOn === 'targetRank' )
        return dataValue.valueMe !== null;
      else if( filterOn === 'targetNotRank' )
        return dataValue.valueMe === null

    // presence and snippets values are selected
    } else {

      if( filterOn === 'onSerp' )
        return values.filter( value => 
          ( dataValue.valueMe !== null && dataValue.valueMe.split( '|' ).includes( value ) )
          || 
          ( dataValue.valueOthers !== null && dataValue.valueOthers.split( '|' ).includes( value ) ) 
        ).length > 0;
      else if( filterOn === 'notOnSerp' )
        return (
          values.filter( value => 
            ( dataValue.valueMe !== null && dataValue.valueMe.split( '|' ).includes( value ) )
            || 
            ( dataValue.valueOthers !== null && dataValue.valueOthers.split( '|' ).includes( value ) )        
          ).length === 0
          ||
          ( dataValue.valueOthers === null && dataValue.valueOthers === null ) 
        );
      else if( filterOn === 'targetRank' )
        return values.filter( value => dataValue.valueMe !== null && dataValue.valueMe.split( '|' ).includes( value ) ).length > 0;
      else if( filterOn === 'targetNotRank' )
        return (
          values.filter( value => dataValue.valueMe !== null && dataValue.valueMe.split( '|' ).includes( value ) ).length === 0 
          && values.filter( value => dataValue.valueOthers !== null && dataValue.valueOthers.split( '|' ).includes( value ) ).length > 0
        );
    }    
  }
}

export const categoriesFilter = ( dataValue, filterValue ) => 
{
  return filterValue.values.filter( elem => dataValue.startsWith( elem ) ).length === 1;
}

export const statusFilter = ( dataValue, filterValue ) => 
{
  return filterValue.values.map( elem => parseInt( elem.value ) ).includes( dataValue );
}

export const keywordsGroupsFilter = ( dataValue, filterValue ) => 
{
  return dataValue !== null && dataValue.includes( filterValue );
}

export const stringInArray = ( dataValue, filterValue ) => 
{
  return Array.isArray( dataValue ) && dataValue.filter( elem => stringFilter( elem.url, filterValue ) ).length > 0;
}

/**
 * Return panel component use for filter custom panel
 * @param {Array} itemsList 
 * @param {Object} column 
 * @returns DOMElement
 */
export const buildDropDownFilterPanel = ( itemsList, column, onFilterFct ) =>
{
  // create filter panel.
  const panel = document.createElement( 'smart-filter-panel' );
  panel.locale = 'fr';
  panel.messages = i18n;
  panel.mode = 'excel';
  panel.dataSource = Array.isArray( itemsList ) && !isNullOrUndefined( itemsList[0]?.label ) ? 
    itemsList
    : itemsList.map( elem => ({ label: elem, value: elem }) );

  // set filter on confirm button
  if( typeof onFilterFct === 'function' )
    panel.onFilter = () => onFilterFct( column, panel, itemsList );

  // unset filter on clear button
  panel.onClear = () => column.filter = null;

  return panel;
}